/**
 * POS API layer. Thin wrapper over the shared axios client so every call
 * carries auth + the org context the backend scopes on.
 */
import api from '../lib/api'

export type VatTreatment = 'standard' | 'zero_rated' | 'exempt' | 'out_of_scope'

export interface PosCategory {
  id: number
  name: string
  name_ar?: string | null
  image_url?: string | null
  sort_order: number
}

export interface PosExtraOption {
  id: number
  name: string
  name_ar?: string | null
  /** VAT-inclusive, added per unit of the line it is chosen on. */
  price: number
  is_available: boolean
}

export interface PosExtraGroup {
  id: number
  name: string
  name_ar?: string | null
  /** 1 or more makes the group REQUIRED — the till must ask before selling. */
  min_options: number
  max_options: number
  options: PosExtraOption[]
}

export interface PosProduct {
  id: number
  name: string
  name_ar?: string | null
  price: number | null
  image_url?: string | null
  category_id?: number | null
  sku?: string | null
  barcode?: string | null
  sort_order: number
  /** Resolved server-side — the till never derives a tax rate itself. */
  vat_rate: number
  vat_treatment: VatTreatment
  is_available: boolean
  /** How many are left today at the catalogue's branch, when set at the till. Null/absent = no limit. */
  available_quantity?: number | null
  /** Sold by weight: the till asks how much, and `price` is per `weight_unit`. */
  is_sold_by_weight?: boolean
  weight_unit?: string | null
  /** Product groups this product is in — a promotion may be scoped by group. */
  group_ids?: number[]
  /** Add-on groups this product offers. Resolved with the catalogue. */
  extra_groups?: PosExtraGroup[]
}

/** A promotion the cashier may pick by hand from the Discount sheet. Only
 *  MANUAL ones travel with the catalogue; automatic ones are the server's. */
export interface PosPromotion {
  id: number
  title: string
  title_ar?: string | null
  offer_type: 'discount_percentage' | 'fixed_discount' | string
  discount_value: number
  /** Picked by a cashier from the Discount sheet; never applied on its own. */
  is_manual?: boolean
  product_ids?: number[] | null
  category_ids?: number[] | null
  group_ids?: number[] | null
  min_order?: number | null
  max_order?: number | null
  start_date?: string | null
  end_date?: string | null
}

/** A delivery app and its price list (Menu → Delivery apps). */
export interface PosDeliveryApp {
  id: number
  name: string
  name_ar?: string | null
  /** product id → price on this app; a product absent sells at its menu price. */
  prices: Record<string, number>
}

/** The product as sold through `app`: its price on the app, else the menu's. */
export const appPriced = <P extends { id: number; price: number | null }>(product: P, app: PosDeliveryApp | null | undefined): P => {
  const price = app?.prices[String(product.id)]
  return price != null ? { ...product, price } : product
}

export interface Catalog {
  categories: PosCategory[]
  products: PosProduct[]
  promotions?: PosPromotion[]
  delivery_apps?: PosDeliveryApp[]
  currency: string
  etag: string
}

/**
 * What a Pocket device is for.
 *
 * One record, three jobs — the same named, coded, branch-scoped thing an
 * operator registers, differing only in what it is permitted to do. Declared
 * here rather than inline so the Devices form, the till and the kitchen
 * display all read the same three words.
 */
export type TerminalRole = 'main' | 'secondary' | 'kds'

export interface Terminal {
  id: number
  name: string
  code: string
  branch_id?: number | null
  /**
   * Where this device is, carried on the device.
   *
   * Saves every caller a second call to /branches just to turn an id into a
   * name — and offline that call cannot be made at all, which is why the
   * kitchen display used to sit on a wall labelled "No branch".
   */
  branch_name?: string | null
  /** Free-text hardware identifier the operator recorded, if any. */
  device_serial?: string | null
  csid_status: string
  zatca_mode: string
  is_active: boolean
  /**
   * 'main' settles sales; 'secondary' only takes orders and hands them to
   * `parent_terminal_id`; 'kds' is a kitchen display, which does neither — it
   * shows its branch's pass. Optional because a backend older than the role
   * migration omits it — absent means main, which is what every terminal was.
   */
  role?: TerminalRole
  parent_terminal_id?: number | null
  last_seen_at?: string | null
  /**
   * Whether a device has actually been set up against this terminal.
   *
   * The one answer that matches what the pairing endpoints enforce: a device
   * that COLLECTED its token. Not `last_seen_at` — that is stamped once per
   * cold start, so a till selling all month reports weeks of silence — and not
   * `paired_at`, which stays behind as history after an unpair.
   */
  is_paired?: boolean
  paired_at?: string | null
  /** The account the device runs as, chosen by whoever approved it. */
  paired_user_name?: string | null
  /** The Pocket APK build the device last reported (null in a browser). */
  app_version?: string | null
  app_version_code?: number | null
  app_channel?: string | null
  /** The release track an operator assigned, overriding app_channel. */
  release_channel?: string | null
}

/** One seat pool. A register, an order-taker and a kitchen display are bought
 *  and activated separately, so a spare display seat does not add a till. */
export interface SeatKindUsage {
  kind: TerminalRole
  used: number
  total: number
  available: number
}

export interface SeatUsage {
  used: number
  total: number
  available: number
  /**
   * False when the org has no cap configured at all.
   *
   * Such an org used to report total === used, i.e. "0 available" forever.
   * Anything that gates on `available` MUST check this first, or every
   * pre-licensing customer is locked out of adding their own devices.
   */
  metered: boolean
  /** True when seats are pooled per kind, in which case `by_kind` decides. */
  per_kind: boolean
  by_kind: SeatKindUsage[]
}

/** Can this org still register a device of this role?
 *
 *  The whole question in one place, because it has three answers depending on
 *  how the org is metered and getting it wrong in either direction is bad: a
 *  false "no" hides the button from someone who has seats, a false "yes" walks
 *  them through a form that ends in a 403.
 */
/** A till, plus how long it may keep trading.
 *
 *  Only `/terminals/me` carries the deadline: it is the till's own question,
 *  and the back office's device list has no use for it — the back office is
 *  already shut by the time it matters.
 */
export interface MyTerminal extends Terminal {
  /** When this till stops trading, if its org's subscription has lapsed.
   *  Null while the subscription is healthy — nothing is counting down. */
  trading_grace_ends_at?: string | null
}

export function seatsAvailableFor(
  seats: SeatUsage | null | undefined, role: TerminalRole,
): boolean {
  if (!seats || !seats.metered) return true
  if (!seats.per_kind) return seats.available > 0
  return (seats.by_kind.find(k => k.kind === role)?.available ?? 0) > 0
}

/** Does this terminal settle its own sales? */
export const terminalSettles = (terminal: Terminal | null | undefined): boolean =>
  (terminal?.role ?? 'main') === 'main'

/** A terminal's role, with the pre-migration default applied. */
export const terminalRole = (terminal: Terminal | null | undefined): TerminalRole =>
  terminal?.role ?? 'main'

/** Is this a kitchen display rather than something a cashier stands at? */
export const isKdsDevice = (terminal: Terminal | null | undefined): boolean =>
  terminalRole(terminal) === 'kds'

export interface Shift {
  id: number
  terminal_id: number
  branch_id?: number | null
  status: 'open' | 'closed'
  opening_float: number
  opened_at?: string | null
  closed_at?: string | null
  counted_cash?: number | null
  expected_cash?: number | null
  variance?: number | null
}

export interface TicketLineExtra {
  extra_option_id?: number | null
  name: string
  name_ar?: string | null
  group_name?: string | null
  price: number
}

export interface TicketLine {
  product_id?: number | null
  name: string
  name_ar?: string | null
  quantity: number
  unit_price: number
  extras: TicketLineExtra[]
  vat_rate: number
  vat_treatment: VatTreatment
  vat_amount: number
  net: number
  gross: number
  /** What a promotion took off this line (VAT-inclusive), and which one. */
  discount?: number
  offer_title?: string | null
}

export interface VatBucket {
  rate: number
  treatment: VatTreatment
  taxable: number
  vat: number
}

export interface Ticket {
  order_id: number
  reference?: string | null
  status: number
  type: number
  /** Walk-in contact snapshotted on the order — not a Customer record. */
  customer_name?: string | null
  customer_phone?: string | null
  lines: TicketLine[]
  total_net: number
  total_discount: number
  /** The promotion applied to the ticket, if any (one at most). */
  offer_title?: string | null
  total_vat: number
  total_gross: number
  vat_breakdown: VatBucket[]
  amount_paid: number
  balance_due: number
  /** The delivery app the order came through, if any. */
  delivery_app_id?: number | null
  delivery_app_name?: string | null
}

// ── Order history ────────────────────────────────────────────────────────────

export interface OrderHistoryRow {
  order_id: number
  reference?: string | null
  table_label?: string | null
  status: number
  type: number
  source: number
  created_at?: string | null
  closed_at?: string | null
  customer_name?: string | null
  customer_phone?: string | null
  cashier_name?: string | null
  item_count: number
  payment_methods: string[]
  invoice_number?: string | null
  /** True when the invoice was issued on a different local calendar day than
   * the order itself (an offline sale synced after the day rolled over) — the
   * dashboard summary and this list can disagree about which day counts this
   * order for exactly this reason. */
  synced_on_different_day?: boolean
  total_net: number
  total_vat: number
  total_gross: number
  amount_paid: number
  balance_due: number
}

export interface OrderPayment {
  method: string
  amount: number
  /** captured | refunded | refund — a `refund` row is money going BACK. */
  status: string
  reference?: string | null
  created_at?: string | null
}

export interface OrderDetail extends OrderHistoryRow {
  branch_name?: string | null
  business_date?: string | null
  notes?: string | null
  check_number?: string | null
  lines: TicketLine[]
  vat_breakdown: VatBucket[]
  payments: OrderPayment[]
  total_discount: number
  rounding_adjustment: number
}

export interface OrderHistoryPage {
  /** How many orders MATCH — a count, for the pager. */
  total: number
  limit: number
  offset: number
  items: OrderHistoryRow[]
  /** Money, over the whole filtered set rather than the current page. */
  completed_total: number
  /** The returned ones only — money that went back out. A void never took
      money, so it is listed in the rows and counted in neither figure. */
  refunded_total: number
  net_total: number
}

export interface OrderHistoryQuery {
  status?: number[]
  type?: number[]
  source?: number
  created_by?: number
  branch_id?: number
  period?: 'day' | 'week' | 'month'
  date?: string
  tz_offset?: number
  search?: string
  limit?: number
  offset?: number
}

export interface InvoiceLine {
  name: string
  name_ar?: string | null
  quantity: number
  unit_price: number
  line_discount: number
  vat_rate: number
  vat_treatment: VatTreatment
  vat_amount: number
  net: number
  gross: number
}

export interface Invoice {
  id: number
  order_id?: number | null
  number: string
  uuid: string
  icv: number
  invoice_type: string
  issued_at: string
  seller_name?: string | null
  seller_name_ar?: string | null
  seller_vat_number?: string | null
  /** False: a receipt from a seller that is not VAT-registered (no VAT, no tax-invoice title). */
  is_tax_invoice?: boolean
  seller_cr_number?: string | null
  seller_address?: string | null
  /** Buyer, when the till captured one. Optional on a simplified invoice. */
  buyer_name?: string | null
  buyer_phone?: string | null
  payments?: OrderPayment[]
  rounding_adjustment?: number
  currency: string
  total_net: number
  total_discount: number
  total_vat: number
  total_gross: number
  vat_breakdown?: VatBucket[] | null
  /** Base64 TLV. Rendered as a QR; never computed in the browser. */
  qr_tlv?: string | null
  signing_mode: string
  /** The INVOICE's state (issued / pending_integration). */
  status: string
  /**
   * Order.status behind this invoice — 3 Done, 5 Returned, 4 Void. An invoice
   * stays 'issued' even on a sale that came back, so this is the only field
   * that says whether the money is still ours.
   */
  order_status?: number | null
  lines: InvoiceLine[]
}

export interface ZReport {
  shift_id: number
  terminal_id: number
  status: string
  opened_at?: string | null
  closed_at?: string | null
  opening_float: number
  cash_sales: number
  pay_ins: number
  pay_outs: number
  expected_cash: number
  counted_cash?: number | null
  variance?: number | null
  totals_by_method: Record<string, number>
  gross_sales: number
  net_sales: number
  vat_collected: number
  invoice_count: number
  credit_note_count: number
  icv_from?: number | null
  icv_to?: number | null
  pending_integration: number
}

/**
 * A payment method code.
 *
 * No longer a closed union: an org can add its own tenders in Settings, and a
 * union would make TypeScript reject a code the operator had just created — as
 * well as a code read back off a payment taken under a method since retired.
 * The known ten stay listed for editor completion; any other string is valid.
 */
export type BuiltinPaymentMethod =
  | 'cash' | 'mada' | 'visa' | 'mastercard' | 'amex'
  | 'apple_pay' | 'stc_pay' | 'transfer' | 'house_account' | 'voucher'
export type PaymentMethod = BuiltinPaymentMethod | (string & {})

/** A row of the org's payment-method catalogue. */
export interface PaymentMethodRow {
  id: number
  code: string
  name: string
  name_ar?: string | null
  is_active: boolean
  /** One of the ten the product ships with: deactivatable, never deletable. */
  is_builtin: boolean
  /** The tender the payment sheet opens on. At most one per org. */
  is_default?: boolean
  sort_order: number
}

// The shared axios client's baseURL already ends in /api/v1.
const base = '/pos'

export const posApi = {
  /** Set how many of a product are left today at a branch (null clears it). */
  setDailyStock: (productId: number, branchId: number, quantity: number | null) =>
    api.put<{ product_id: number; branch_id: number; available_quantity: number | null }>(
      `${base}/catalog/products/${productId}/daily-stock`, { branch_id: branchId, quantity },
    ).then(r => r.data),
  getCatalog: (branchId?: number) =>
    api.get<Catalog>(`${base}/catalog`, { params: branchId ? { branch_id: branchId } : {} })
      .then(r => r.data),

  listTerminals: () =>
    api.get<Terminal[]>(`${base}/terminals`).then(r => r.data),

  /** Which terminal THIS session is, for a paired till.
   *
   *  Separate from listTerminals because they are separate questions with
   *  separate lifetimes: the org's device list is back office and closes with
   *  it, while a till's own identity stays answerable as long as the till may
   *  trade. 404 when a person asks — a browser session is not a terminal. */
  myTerminal: () =>
    api.get<MyTerminal>(`${base}/terminals/me`).then(r => r.data),

  createTerminal: (body: {
    name: string; code: string; branch_id?: number | null
    role?: TerminalRole; parent_terminal_id?: number | null
  }) => api.post<Terminal>(`${base}/terminals`, body).then(r => r.data),

  currentShift: (terminalId: number) =>
    api.get<Shift | null>(`${base}/shifts/current`, { params: { terminal_id: terminalId } })
      .then(r => r.data),

  /** The newest closed shift on this terminal — the till's "not closed" warning. */
  lastClosedShift: (terminalId: number) =>
    api.get<Shift | null>(`${base}/shifts/last-closed`, { params: { terminal_id: terminalId } })
      .then(r => r.data),

  openShift: (terminalId: number, openingFloat: number) =>
    api.post<Shift>(`${base}/shifts`, { terminal_id: terminalId, opening_float: openingFloat })
      .then(r => r.data),

  shiftReport: (shiftId: number) =>
    api.get<ZReport>(`${base}/shifts/${shiftId}/report`).then(r => r.data),

  addCashMovement: (shiftId: number, body: { kind: string; amount: number; reason?: string }) =>
    api.post(`${base}/shifts/${shiftId}/movements`, body).then(r => r.data),

  closeShift: (shiftId: number, countedCash: number, notes?: string) =>
    api.post<ZReport>(`${base}/shifts/${shiftId}/close`,
      { counted_cash: countedCash, notes }).then(r => r.data),

  createTicket: (body: {
    terminal_id: number
    branch_id?: number | null
    type?: number
    table_label?: string | null
    customer_id?: number
    customer_name?: string | null
    customer_phone?: string | null
    items: { product_id: number; quantity: number; option_ids?: number[] }[]
    delivery_app_id?: number | null
  }) => api.post<Ticket>(`${base}/tickets`, body).then(r => r.data),

  getTicket: (orderId: number) =>
    api.get<Ticket>(`${base}/tickets/${orderId}`).then(r => r.data),

  listOpenTickets: () =>
    api.get<Ticket[]>(`${base}/tickets`).then(r => r.data),

  /**
   * Moves an open ticket between Pending (1) and Active (2) — the board's drag.
   * The server refuses anything else: Done comes from payment, Cancelled from a
   * void, Returned from a refund.
   */
  setTicketStatus: (orderId: number, status: number) =>
    api.patch<Ticket>(`${base}/tickets/${orderId}/status`, { status }).then(r => r.data),

  /** Cancels the ticket. The order is KEPT (status 4) and stays in history. */
  voidTicket: (orderId: number) =>
    api.delete<Ticket>(`${base}/tickets/${orderId}`).then(r => r.data),

  /** A cashier's discount — a saved manual promotion, or an ad-hoc percent /
   *  amount on the whole bill or the named products. Replaces any automatic
   *  promotion; the server prices it. */
  discountTicket: (orderId: number, body: {
    offer_id?: number; percent?: number; amount?: number; label?: string; product_ids?: number[]
  }) => api.post<Ticket>(`${base}/tickets/${orderId}/discount`, body).then(r => r.data),
  clearDiscount: (orderId: number) =>
    api.delete<Ticket>(`${base}/tickets/${orderId}/discount`).then(r => r.data),

  /**
   * Reverses a completed sale (status 5). `method` is required — a returned
   * order was already paid for, so the money has to go back some way, and the
   * drawer count needs to know which. Issues the credit note when the order
   * was invoiced.
   */
  returnTicket: (orderId: number, body: {
    method: PaymentMethod
    reason_code?: 'return' | 'error' | 'cancellation' | 'goodwill'
    reason_text?: string
    /** The terminal processing this return, so the refund is attributed to
     * whatever shift is open on it now rather than the original sale's shift. */
    terminal_id?: number
  }) => api.post<Ticket>(`${base}/tickets/${orderId}/return`, body).then(r => r.data),

  orderDetail: (orderId: number) =>
    api.get<OrderDetail>(`${base}/orders/${orderId}`).then(r => r.data),

  orderHistory: (query: OrderHistoryQuery) => {
    // Built by hand rather than handed to axios: FastAPI reads repeated keys
    // (?status=3&status=5) and axios' default array format is `status[]=`,
    // which arrives as an unknown parameter and silently drops the filter.
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) value.forEach(v => params.append(key, String(v)))
      else params.append(key, String(value))
    }
    return api.get<OrderHistoryPage>(`${base}/orders?${params.toString()}`).then(r => r.data)
  },

  // `terminal_id` travels with the tender so the server can refuse one taken
  // on a secondary terminal — a device with no drawer must not open a cash
  // position.
  /**
   * "This device is still here."
   *
   * The endpoint has existed since terminals did and had no caller, which is
   * why `last_seen_at` was stamped once per cold start and read as weeks of
   * silence on a till that had been selling all month. apps/pocket calls it on
   * an interval; the back office only reads the column.
   */
  /** `build` is the Pocket APK this device runs, when it runs in one — what
   *  the internal admin's version-adoption table and the update gate read.
   *  Omitted in a browser, which has no APK. */
  heartbeat: (terminalId: number, build?: { app_version: string; app_version_code: number; app_channel: string }) =>
    api.post<Terminal>(`${base}/terminals/${terminalId}/heartbeat`, build).then(r => r.data),

  /** Cashier seats: how many this org has, and how many are in use. */
  seatUsage: () =>
    api.get<SeatUsage>(`${base}/terminals/seats`).then(r => r.data),

  // ── Payment methods ──────────────────────────────────────────────────────
  listPaymentMethods: () =>
    api.get<PaymentMethodRow[]>(`${base}/payment-methods`).then(r => r.data),
  createPaymentMethod: (body: { code: string; name: string; name_ar?: string }) =>
    api.post<PaymentMethodRow>(`${base}/payment-methods`, body).then(r => r.data),
  updatePaymentMethod: (id: number, body: {
    name?: string; name_ar?: string | null; is_active?: boolean; is_default?: boolean; sort_order?: number
  }) => api.patch<PaymentMethodRow>(`${base}/payment-methods/${id}`, body).then(r => r.data),
  deletePaymentMethod: (id: number) =>
    api.delete(`${base}/payment-methods/${id}`).then(r => r.data),

  takePayment: (orderId: number, body: {
    method: PaymentMethod; amount: number; tendered?: number; reference?: string
    terminal_id?: number
  }) => api.post(`${base}/tickets/${orderId}/payments`, body).then(r => r.data),

  issueInvoice: (orderId: number, terminalId: number) =>
    api.post<Invoice>(`${base}/tickets/${orderId}/invoice`, null,
      { params: { terminal_id: terminalId } }).then(r => r.data),

  listInvoices: (shiftId?: number) =>
    api.get<Invoice[]>(`${base}/invoices`, { params: shiftId ? { shift_id: shiftId } : {} })
      .then(r => r.data),

  /**
   * Land a till's queue of sales taken while it was disconnected.
   *
   * Returns the raw axios response rather than `.data`, because the caller
   * needs the whole envelope: every order reports its own outcome and one
   * failure inside a 200 is the normal case, not an error to throw on.
   */
  syncOfflineOrders: (body: {
    terminal_id: number
    orders: unknown[]
  }) => api.post<{ results: any[] }>(`${base}/tickets/sync`, body),

  creditNote: (invoiceId: number, reasonCode: string, reasonText?: string) =>
    api.post(`${base}/invoices/${invoiceId}/credit-note`,
      { reason_code: reasonCode, reason_text: reasonText }).then(r => r.data),
}

// ── Kitchen display ──────────────────────────────────────────────────────────

/**
 * Where a ticket is on the pass.
 *
 * A different axis from `Order.status`, and deliberately so: status is the
 * MONEY (pending, paid, void, returned) and says nothing about whether the
 * food has been made. A counter shop takes payment first, so a paid coffee is
 * status 3 while it is still to pour.
 *
 * 'served' is the bump — it takes the ticket off the board without touching
 * the sale, which is why it is a state and not a delete.
 */
export type PrepState = 'queued' | 'preparing' | 'ready' | 'served'

/** The lanes the board draws, in the order a ticket walks them. */
export const PREP_LANES: { state: Exclude<PrepState, 'served'>; label: string }[] = [
  { state: 'queued',    label: 'New' },
  { state: 'preparing', label: 'Preparing' },
  { state: 'ready',     label: 'Ready' },
]

export interface KdsLine {
  name: string
  name_ar?: string | null
  quantity: number
  /** Add-ons, flattened to names — "oat milk, extra shot". */
  options: string[]
}

export interface KdsTicket {
  order_id: number
  reference?: string | null
  type: number
  table_label?: string | null
  customer_name?: string | null
  branch_id?: number | null
  prep_state: PrepState
  created_at?: string | null
  prep_changed_at?: string | null
  /** The order's money status, so a settled ticket can say so. Never gates
      what the kitchen may do with it. */
  status: number
  item_count: number
  notes?: string | null
  lines: KdsLine[]
  /** Rung up while the till was offline and sent on reconnect (so it arrives
   *  late); `created_at` is still when it was taken. Absent on older servers. */
  taken_offline?: boolean
}

export const kdsApi = {
  /**
   * The branch's pass, oldest first.
   *
   * `terminalId` is the usual call: the display is registered against a branch
   * and the server reads it off the device, so a screen bolted to a wall has
   * one less thing to be configured wrongly with.
   */
  tickets: (params: { terminalId?: number; branchId?: number } = {}) =>
    api.get<KdsTicket[]>(`${base}/kds/tickets`, {
      params: { terminal_id: params.terminalId, branch_id: params.branchId },
    }).then(r => r.data),

  setState: (orderId: number, state: PrepState) =>
    api.patch<KdsTicket>(`${base}/kds/tickets/${orderId}`, { state }).then(r => r.data),
}

// ── Cashier identity ─────────────────────────────────────────────────────────
export interface Cashier {
  id: number
  name: string
  /** May give manual discounts — from the unlock or the offline roster. */
  can_discount?: boolean
}

export interface CashierUnlock {
  cashier: Cashier
  token: string
}

// ── Dashboard insights ───────────────────────────────────────────────────────
export type InsightPeriod = 'day' | 'week' | 'month'

export interface InsightPoint { t: string; v: number }

export interface InsightMetric {
  value: number
  /** null unless `compare` was requested — absent is not the same as zero. */
  previous: number | null
  series: InsightPoint[]
}

export interface Insights {
  period: InsightPeriod
  start: string
  end: string
  currency: string
  orders: InsightMetric
  net_sales: InsightMetric
  net_payments: InsightMetric
}

/** A cashier as the back office sees them. */
export interface CashierRosterRow {
  id: number
  name: string
  email: string
  role: string
  is_active: boolean
  has_pin: boolean
  /** Made without an address — the email is a placeholder nobody reads. */
  placeholder_email: boolean
  branch_ids: number[]
}
export interface CashierCreated extends CashierRosterRow {
  /** In the clear, exactly once. */
  pos_pin: string
}

export const cashierApi = {
  roster: () => api.get<Cashier[]>(`${base}/cashiers`).then(r => r.data),

  // Back office: everyone on the till, and the one-step way to add someone.
  manageRoster: () => api.get<CashierRosterRow[]>(`${base}/cashiers/roster`).then(r => r.data),
  quickCreate: (body: { name: string; role?: string; email?: string; branch_ids?: number[] }) =>
    api.post<CashierCreated>(`${base}/cashiers`, body).then(r => r.data),
  resetPin: (userId: number) =>
    api.post<CashierCreated>(`${base}/cashiers/${userId}/pin`).then(r => r.data),

  unlock: (pin: string) =>
    api.post<CashierUnlock>(`${base}/unlock`, { pin }).then(r => r.data),
}

export const insightsApi = {
  get: (params: {
    period: InsightPeriod
    date?: string
    compare?: boolean
    branchId?: number
  }) => api.get<Insights>(`${base}/insights`, {
    params: {
      period: params.period,
      date: params.date,
      compare: params.compare || undefined,
      branch_id: params.branchId,
      // The trading day is local. The server buckets against this rather than
      // assuming UTC, so an 11pm sale lands on the evening it was rung up.
      tz_offset: -new Date().getTimezoneOffset(),
    },
  }).then(r => r.data),
}

/** Orders list, scoped to the same trading window the dashboard counts. */
export const ordersApi = {
  list: (params: { period?: InsightPeriod; date?: string; limit?: number; branchId?: number }) =>
    api.get<Invoice[]>(`${base}/invoices`, {
      params: {
        period: params.period,
        date: params.date,
        limit: params.limit ?? 100,
        branch_id: params.branchId,
        tz_offset: -new Date().getTimezoneOffset(),
      },
    }).then(r => r.data),
}

export const shiftsApi = {
  list: (params: { status?: 'open' | 'closed'; limit?: number } = {}) =>
    api.get<Shift[]>(`${base}/shifts`, {
      params: { status: params.status, limit: params.limit ?? 50 },
    }).then(r => r.data),

  report: (shiftId: number) => posApi.shiftReport(shiftId),
}

/**
 * CSV of the rows currently filtered, not of everything.
 *
 * Fetched through the shared client rather than linked to. A bare `<a href>`
 * at the API origin is a top-level navigation: it cannot run the refresh
 * interceptor, so the moment the access cookie has aged out the operator lands
 * on a raw `401 Unauthorized` page instead of a download — outside the app,
 * with the filters they had set gone.
 */
export async function ordersExportCsv(params: {
  period?: InsightPeriod; date?: string; branchId?: number
}): Promise<string> {
  const r = await api.get(`${base}/invoices/export`, {
    params: {
      period: params.period,
      date: params.date,
      branch_id: params.branchId,
      tz_offset: -new Date().getTimezoneOffset(),
    },
    responseType: 'text',
  })
  return r.data as string
}


// ── Device pairing, from the back office ────────────────────────────────────
//
// The device's own half of this lives in lib/deviceSession.ts and is
// deliberately separate: those three calls are unauthenticated and run before
// anyone is signed in, and mixing them into the authenticated API surface is
// how one accidentally grows a dependency on the other.

export interface PendingPairing {
  terminal_id: number
  terminal_name: string
  terminal_code: string
  branch_name: string | null
  role: string
  pairing_code: string
  expires_at: string
}

export const pairingApi = {
  pending: () => api.get<PendingPairing[]>('/pos/pairing/pending').then(r => r.data),

  // `userId` omitted means the device runs as whoever approves it, which is
  // the answer for a shop with one organization. The parameter stays so a
  // dedicated device account is still expressible without a round trip
  // through the backend again.
  approve: (terminalId: number, pairingCode: string, userId?: number) =>
    api.post(`/pos/pairing/${terminalId}/approve`, {
      pairing_code: pairingCode,
      ...(userId ? { user_id: userId } : {}),
    }),

  revoke: (terminalId: number) => api.post(`/pos/pairing/${terminalId}/revoke`),
}
